import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface WhatsAppSettings {
  id: string;
  phone_number: string;
  display_name: string;
  welcome_message: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export const useWhatsAppSettings = () => {
  return useQuery({
    queryKey: ["whatsapp-settings"],
    queryFn: async () => {
      // Use SECURITY DEFINER RPC so anonymous users can fetch only the safe display fields
      // (the underlying table is restricted to admins).
      const { data, error } = await supabase.rpc("get_active_whatsapp_settings");

      if (error) throw error;
      const row = Array.isArray(data) ? data[0] : data;
      return (row ?? null) as WhatsAppSettings | null;
    },
  });
};

export const useUpdateWhatsAppSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...settings }: Partial<WhatsAppSettings> & { id: string }) => {
      const { data, error } = await supabase
        .from("whatsapp_settings")
        .update(settings)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["whatsapp-settings"] });
      toast.success("WhatsApp settings updated");
    },
    onError: (error) => {
      toast.error("Failed to update settings: " + error.message);
    },
  });
};

export const generateWhatsAppLink = (
  phoneNumber: string,
  message: string
): string => {
  const cleanNumber = phoneNumber.replace(/[^0-9]/g, "");
  const encodedMessage = encodeURIComponent(message);
  return `https://wa.me/${cleanNumber}?text=${encodedMessage}`;
};

export const generateOrderWhatsAppMessage = (orderNumber: string): string => {
  return `Hi! I have a question about my order ${orderNumber}. Can you help me?`;
};

export const generateProductWhatsAppMessage = (productName: string): string => {
  return `Hi! I'm interested in "${productName}". Can you tell me more about it?`;
};

export const generateGeneralWhatsAppMessage = (): string => {
  return `Namaste! 🙏 I would like to know more about your handcrafted products.`;
};
